import {
  AuthenticateEncounterArgs,
  CompleteGynIntakeFormArgs,
  CreateGynIntakeCodeArgs,
  DeleteEncounterArgs,
  Encounter,
  ExchangeGynIntakeCodeArgs,
  ExchangeGynIntakeCodeResult,
  GetGynIntakeFormDataArgs,
  GynIntakeFormData,
  LogEncounterViewArgs,
  PlaceEncounterBillingHoldArgs,
  RespondToSupplyLogArgs,
  SaveColorTagArgs,
  SaveContactLogArgs,
  SaveEncounterArgs,
  SaveLabResultArgs,
  SaveProviderFlagArgs,
  SendGynInviteEmailArgs,
  SubmitPatientStatementArgs,
  UpdateEncounterBillingFieldsArgs,
  UpdateGynAnswersArgs,
  UpdateGynAnswersByCodeArgs,
  UpdateGynAnswersByCodeResult,
} from '@hb/shared'
import { Functions, httpsCallable } from 'firebase/functions'
import { functions } from './functions'

export const sendGynInviteEmail = httpsCallable<SendGynInviteEmailArgs>(
  functions,
  'sendGynInviteEmail',
)

export const saveEncounter = httpsCallable<
  SaveEncounterArgs,
  { id: string; encounter: Encounter; success: string }
>(functions, 'saveEncounter')

export const saveContactLog = httpsCallable<SaveContactLogArgs, { id: string; success: string }>(
  functions,
  'saveContactLog',
)

export const saveLabResult = httpsCallable<SaveLabResultArgs, { id: string; success: string }>(
  functions,
  'saveLabResult',
)

export const saveProviderFlag = httpsCallable<SaveProviderFlagArgs, { success: string }>(
  functions,
  'saveProviderFlag',
)

export const saveColorTag = httpsCallable<SaveColorTagArgs, { success: string }>(
  functions,
  'saveColorTag',
)

export const authenticateEncounter = httpsCallable<
  AuthenticateEncounterArgs,
  { id: string; encounter: Encounter; success: string }
>(functions, 'authenticateEncounter')

export const deleteEncounter = httpsCallable<DeleteEncounterArgs, { id: string; success: string }>(
  functions,
  'deleteEncounter',
)

// Admin-app billing metadata writes (billing checklist, supply-log CDI responses) —
// Firestore rules allow no client writes to ENCOUNTERS, so these go through a callable.
export const updateEncounterBillingFields = httpsCallable<
  UpdateEncounterBillingFieldsArgs,
  { success: string }
>(functions, 'updateEncounterBillingFields')

// §1.6.2 Hold from Billing on encounters — provider-only (place + release); the callable
// authorizes against the encounter's stored practiceId, so no practiceId is passed.
export const placeEncounterBillingHold = httpsCallable<
  PlaceEncounterBillingHoldArgs,
  { success: boolean }
>(functions, 'placeEncounterBillingHold')

// §6.7 provider-only supply-log responses — records the provider's Yes/No CDI decision
// and the "No billable supplies this visit" pre-auth gate. Provider-permitted write path
// (authorizes against the encounter's stored practiceId), so no practiceId is passed.
export const respondToSupplyLog = httpsCallable<RespondToSupplyLogArgs, { success: boolean }>(
  functions,
  'respondToSupplyLog',
)

// §7.1 audit logging of note views. Fire-and-forget from the client (never blocks or
// breaks note opening — see useEncounterOpenState / ClaimReviewScreen); the server derives
// user id, IP, timestamp, action, and the denormalized patient/assessment/practice ids
// from the stored encounter, so only encounterId + appName are sent.
export const logEncounterView = httpsCallable<LogEncounterViewArgs, { success: boolean }>(
  functions,
  'logEncounterView',
)

export const createGynIntakeCode = httpsCallable<
  CreateGynIntakeCodeArgs,
  { code: string; expiresAt: number }
>(functions, 'createGynIntakeCode')

export const getGynIntakeFormData = (appFunctions: Functions) =>
  httpsCallable<GetGynIntakeFormDataArgs, GynIntakeFormData>(appFunctions, 'getGynIntakeFormData')

export const updateGynAnswersByCode = (appFunctions: Functions) =>
  httpsCallable<UpdateGynAnswersByCodeArgs, UpdateGynAnswersByCodeResult>(
    appFunctions,
    'updateGynAnswersByCode',
  )

export const exchangeGynIntakeCode = (appFunctions: Functions) =>
  httpsCallable<ExchangeGynIntakeCodeArgs, ExchangeGynIntakeCodeResult>(
    appFunctions,
    'exchangeGynIntakeCode',
  )

export const completeGynIntakeForm = (appFunctions: Functions) =>
  httpsCallable<CompleteGynIntakeFormArgs, { success: boolean }>(
    appFunctions,
    'completeGynIntakeForm',
  )

export const updateGynAnswers = (appFunctions: Functions) =>
  httpsCallable<UpdateGynAnswersArgs, string>(appFunctions, 'updateGynAnswers')

export const submitPatientStatement = httpsCallable<
  SubmitPatientStatementArgs,
  { success: boolean; id: string }
>(functions, 'submitPatientStatement')
